import { useEffect, useState } from 'react';
import { get } from 'lodash';
import {
  Box, Card, Container, Stack, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, CircularProgress,
  Alert, Breadcrumbs, Link, Collapse, Divider, Pagination,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Page from '../../components/Page';
import { getOmrScanLogs, getOmrScanLogDetail } from '../../axiosLib/services/omrScanLog/admin';

// ── Helpers ──

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const mon = months[d.getMonth()];
  const year = d.getFullYear();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${day} ${mon} ${year}, ${time}`;
};

const getOmrTypeColor = (type) => {
  if (!type) return '#6366f1';
  const t = type.toLowerCase();
  if (t.includes('online')) return '#22c55e';
  if (t.includes('offline')) return '#f59e0b';
  if (t.includes('bypass')) return '#ef4444';
  return '#6366f1';
};

// ── Raw Data Viewer ──

const RawDataViewer = ({ data }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  if (!Array.isArray(data)) {
    return (
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#1e1b4b', overflow: 'auto', maxHeight: 500 }}>
        <pre style={{ color: '#e2e8f0', fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#475569', mb: 1.5 }}>
        {data.length} entries scanned
      </Typography>
      <TableContainer component={Paper} sx={{
        borderRadius: 2, border: '1px solid #e2e8f0',
        maxHeight: 600, overflow: 'auto',
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.75rem', color: '#475569' } }}>
              <TableCell width={40}>#</TableCell>
              <TableCell>Roll</TableCell>
              <TableCell>Set</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Found</TableCell>
              <TableCell>Re-scan</TableCell>
              <TableCell>MCQ Count</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((entry, idx) => (
              <>
                <TableRow
                  key={idx}
                  hover
                  onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    bgcolor: expandedIdx === idx ? '#f0f9ff' : 'transparent',
                    '&:hover': { bgcolor: '#f0f9ff' },
                  }}
                >
                  <TableCell sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem', fontFamily: 'monospace' }}>
                    {entry.roll || '—'}
                  </TableCell>
                  <TableCell>
                    {entry.set ? (
                      <Chip label={entry.set} size="small" sx={{
                        fontWeight: 700, fontSize: '0.7rem', height: 22,
                        bgcolor: '#ede9fe', color: '#6d28d9',
                      }} />
                    ) : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{entry.name || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{entry.phone || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={entry.found ? 'Yes' : 'No'}
                      size="small"
                      sx={{
                        fontWeight: 600, fontSize: '0.68rem', height: 20,
                        bgcolor: entry.found ? '#dcfce7' : '#fee2e2',
                        color: entry.found ? '#166534' : '#991b1b',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {entry.re_scan && (
                      <Chip label="Re-scan" size="small" sx={{
                        fontWeight: 600, fontSize: '0.68rem', height: 20,
                        bgcolor: '#fef3c7', color: '#92400e',
                      }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: '#6366f1', fontWeight: 600 }}>
                    {Array.isArray(entry.mcq) ? entry.mcq.length : 0}
                  </TableCell>
                  <TableCell>
                    <Icon
                      icon={expandedIdx === idx ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                      style={{ fontSize: 18, color: '#94a3b8' }}
                    />
                  </TableCell>
                </TableRow>
                {expandedIdx === idx && (
                  <TableRow key={`detail-${idx}`}>
                    <TableCell colSpan={9} sx={{ p: 0, borderBottom: '2px solid #e2e8f0' }}>
                      <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
                        {/* MCQ Answers Grid */}
                        {Array.isArray(entry.mcq) && entry.mcq.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', mb: 1 }}>
                              MCQ Answers
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {entry.mcq.map((ans, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: 36, height: 30, display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', borderRadius: 1, fontSize: '0.72rem',
                                    fontWeight: 700, fontFamily: 'monospace',
                                    bgcolor: ans ? '#dbeafe' : '#f1f5f9',
                                    color: ans ? '#1e40af' : '#cbd5e1',
                                    border: `1px solid ${ans ? '#93c5fd' : '#e2e8f0'}`,
                                  }}
                                  title={`Q${i + 1}`}
                                >
                                  {ans || '·'}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ── Main Component ──

export default function OmrScanLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = () => {
    setLoading(true);
    setError(null);
    getOmrScanLogs({ page, limit: 20 }, (err, data) => {
      setLoading(false);
      if (err) {
        setError(typeof err === 'string' ? err : 'Unable to load scan logs. Make sure you have SUPER_ADMIN access.');
        return;
      }
      setLogs(get(data, 'logs', []));
      setTotalPages(get(data, 'pages', 1));
      setTotal(get(data, 'total', 0));
    });
  };

  const handleViewDetail = (logId) => {
    setDetailLoading(true);
    getOmrScanLogDetail(logId, (err, data) => {
      setDetailLoading(false);
      if (err) {
        setError('Failed to load scan log details');
        return;
      }
      setSelectedLog(get(data, 'log', null));
    });
  };

  const handleBack = () => {
    setSelectedLog(null);
  };

  // ── Detail View ──
  if (selectedLog) {
    return (
      <Page title="OMR Scan Log Detail">
        <Container maxWidth="xl" sx={{ pt: 3, pb: 5 }}>
          {/* Breadcrumb */}
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer', fontSize: '0.85rem' }}
              onClick={handleBack}
            >
              Scan Logs
            </Link>
            <Typography color="text.primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Detail
            </Typography>
          </Breadcrumbs>

          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <IconButton onClick={handleBack} sx={{
              bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' },
            }}>
              <Icon icon="mdi:arrow-left" style={{ fontSize: 20 }} />
            </IconButton>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e1b4b' }}>
                Scan Log Detail
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                ID: {selectedLog._id}
              </Typography>
            </Box>
          </Stack>

          {/* Metadata Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2, mb: 3 }}>
            <MetaCard
              icon="mdi:account"
              label="Scanned By"
              value={
                selectedLog.userId
                  ? `${get(selectedLog, 'userId.firstname', '')} ${get(selectedLog, 'userId.lastname', '')}`.trim() || get(selectedLog, 'userId.username', '—')
                  : '—'
              }
            />
            <MetaCard icon="mdi:clock-outline" label="Scan Time" value={formatDate(selectedLog.scanTime)} />
            <MetaCard icon="mdi:format-list-numbered" label="Entry Count" value={selectedLog.entryCount || 0} />
            <MetaCard
              icon="mdi:barcode-scan"
              label="OMR Type"
              value={selectedLog.omrType || 'unknown'}
              chipColor={getOmrTypeColor(selectedLog.omrType)}
            />
            <MetaCard
              icon="mdi:domain"
              label="Institution"
              value={get(selectedLog, 'institution.institutionName', selectedLog.institution) || '—'}
            />
            <MetaCard icon="mdi:calendar" label="Created At" value={formatDate(selectedLog.createdAt)} />
            <MetaCard icon="mdi:calendar-clock" label="Session" value={selectedLog.session || '—'} chipColor="#8b5cf6" />
            <MetaCard
              icon="mdi:file-document-outline"
              label="Exam"
              value={get(selectedLog, 'examId.name', '') || '—'}
              chipColor="#0ea5e9"
            />
          </Box>

          {/* Raw Data */}
          <Card sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.08)' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Box sx={{
                width: 38, height: 38, borderRadius: 2, display: 'flex',
                alignItems: 'center', justifyContent: 'center', bgcolor: '#ede9fe',
              }}>
                <Icon icon="mdi:code-json" style={{ fontSize: 20, color: '#6d28d9' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e1b4b' }}>
                Raw Scan Data
              </Typography>
            </Stack>
            <RawDataViewer data={selectedLog.rawData} />
          </Card>
        </Container>
      </Page>
    );
  }

  // ── List View ──
  return (
    <Page title="OMR Scan Logs">
      <Container maxWidth="xl" sx={{ pt: 3, pb: 5 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e1b4b' }}>
              OMR Scan Logs
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', mt: 0.5 }}>
              View all OMR scan session logs from the desktop app
            </Typography>
          </Box>
          <IconButton onClick={fetchLogs} sx={{
            bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' },
          }}>
            <Icon icon="mdi:refresh" style={{ fontSize: 22 }} />
          </IconButton>
        </Stack>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
        )}

        {/* Loading */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : logs.length === 0 ? (
          <Card sx={{ p: 6, borderRadius: 3, textAlign: 'center', border: '1px solid #e2e8f0' }}>
            <Icon icon="mdi:file-document-outline" style={{ fontSize: 48, color: '#cbd5e1' }} />
            <Typography sx={{ mt: 2, fontWeight: 600, color: '#94a3b8' }}>No scan logs found</Typography>
          </Card>
        ) : (
          <>
          <Card sx={{ borderRadius: 3, border: '1px solid rgba(99,102,241,0.08)', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 700 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': {
                    bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.78rem', color: '#475569',
                    borderBottom: '2px solid #e2e8f0',
                  } }}>
                    <TableCell width={40}>#</TableCell>
                    <TableCell>Scanned By</TableCell>
                    <TableCell>OMR Type</TableCell>
                    <TableCell>Entries</TableCell>
                    <TableCell>Scan Time</TableCell>
                    <TableCell>Session</TableCell>
                    <TableCell>Exam</TableCell>
                    <TableCell>Institution</TableCell>
                    <TableCell width={50} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log, idx) => (
                    <TableRow
                      key={log._id}
                      hover
                      onClick={() => handleViewDetail(log._id)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: '#f0f9ff' },
                        '& td': { fontSize: '0.82rem', py: 1.8 },
                      }}
                    >
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>{(page - 1) * 20 + idx + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '50%', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 700, fontSize: '0.75rem',
                          }}>
                            {(get(log, 'userId.firstname', '?')[0] || '?').toUpperCase()}
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e1b4b' }}>
                              {log.userId
                                ? `${get(log, 'userId.firstname', '')} ${get(log, 'userId.lastname', '')}`.trim() || get(log, 'userId.username', '—')
                                : '—'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                              {get(log, 'userId.phone', '')}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.omrType || 'unknown'}
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: '0.7rem', height: 24,
                            bgcolor: `${getOmrTypeColor(log.omrType)}15`,
                            color: getOmrTypeColor(log.omrType),
                            border: `1px solid ${getOmrTypeColor(log.omrType)}30`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{
                          fontWeight: 700, fontSize: '0.9rem', color: '#6366f1',
                        }}>
                          {log.entryCount || 0}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#475569' }}>{formatDate(log.scanTime)}</TableCell>
                      <TableCell sx={{ color: '#8b5cf6', fontWeight: 600 }}>
                        {log.session || '—'}
                      </TableCell>
                      <TableCell sx={{ color: '#0ea5e9', fontSize: '0.8rem' }}>
                        {get(log, 'examId.name', '') || '—'}
                      </TableCell>
                      <TableCell sx={{ color: '#64748b' }}>
                        {get(log, 'institution.institutionName', '—')}
                      </TableCell>
                      <TableCell>
                        {detailLoading ? (
                          <CircularProgress size={16} sx={{ color: '#6366f1' }} />
                        ) : (
                          <Icon icon="mdi:chevron-right" style={{ fontSize: 20, color: '#94a3b8' }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, px: 1 }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                {total} log{total !== 1 ? 's' : ''} total
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
                shape="rounded"
                size="medium"
              />
            </Stack>
          )}
          </>
        )}
      </Container>
    </Page>
  );
}

// ── Small Meta Card ──

function MetaCard({ icon, label, value, chipColor }) {
  return (
    <Card sx={{
      p: 2, borderRadius: 2.5, border: '1px solid #f1f5f9',
      transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' },
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 2, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          bgcolor: chipColor ? `${chipColor}15` : '#f1f5f9',
        }}>
          <Icon icon={icon} style={{ fontSize: 18, color: chipColor || '#6366f1' }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{
            fontWeight: 700, fontSize: '0.88rem', color: '#1e1b4b',
            wordBreak: 'break-word',
          }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}
